import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import {
  Card,
  EmptyState,
  ErrorState,
  Loading,
  PageHeader,
  ScoreBadge,
  Select,
} from '@/components/ui';
import { formatNumber, formatPercent, formatService } from '@/lib/format';

interface Qualification {
  window_days: number;
  clients: Array<{
    client_name: string;
    tenant_id: string;
    assessed: number;
    qualified: number;
    avg_score: number;
  }>;
  services: Array<{ service: string; leads: number; qualified: number }>;
  missing_fields: Array<{ field: string; times_missing: number }>;
}

/**
 * What the bots are actually collecting, and where they stall.
 *
 * The missing-fields panel is the one to act on: a field absent from most
 * conversations is nearly always a question the engine asks badly, not a run of
 * uncooperative customers.
 */
export default function Qualification() {
  const [days, setDays] = useState(30);

  const query = useQuery({
    queryKey: ['admin-qualification', days],
    queryFn: () => api.get<Qualification>('/admin/qualification', { days }),
  });

  if (query.isPending) return <Loading />;
  if (query.error) return <ErrorState error={query.error} retry={() => query.refetch()} />;

  const { clients, services, missing_fields: missing } = query.data;
  const topService = services[0]?.leads ?? 0;
  const mostMissing = missing[0]?.times_missing ?? 0;

  return (
    <div className="w-full">
      <PageHeader
        title="Qualification"
        description="What the bots collect, which services they find, and which questions go unanswered."
        actions={
          <Select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="w-auto"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </Select>
        }
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="By client" description="Assessed conversations and how many passed." padded={false}>
          {clients.length === 0 ? (
            <EmptyState title="Nothing assessed yet" description="Qualification results appear after calls run." />
          ) : (
            <div className="scroll-x">
              <table className="glee-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th className="text-right">Assessed</th>
                    <th className="text-right">Qualified</th>
                    <th className="text-right">Rate</th>
                    <th className="text-right">Avg score</th>
                  </tr>
                </thead>
                <tbody>
                  {clients.map((row) => (
                    <tr key={row.tenant_id}>
                      <td>
                        <Link
                          to={`/clients/${row.tenant_id}`}
                          className="font-medium text-ink-900 hover:text-brand-600 dark:text-ink-50 dark:hover:text-brand-400"
                        >
                          {row.client_name}
                        </Link>
                      </td>
                      <td className="text-right tabular-nums text-ink-700 dark:text-ink-200">
                        {formatNumber(row.assessed)}
                      </td>
                      <td className="text-right tabular-nums text-ink-700 dark:text-ink-200">
                        {formatNumber(row.qualified)}
                      </td>
                      <td className="text-right tabular-nums text-ink-700 dark:text-ink-200">
                        {formatPercent(row.assessed ? (row.qualified / row.assessed) * 100 : 0)}
                      </td>
                      <td className="text-right">
                        <ScoreBadge score={row.avg_score} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card
          title="Questions going unanswered"
          description="A field missing this often is usually the engine's fault, not the customer's."
        >
          {missing.length === 0 ? (
            <p className="py-8 text-center text-sm text-ink-500">
              Nothing is being missed. Every required field is coming back filled.
            </p>
          ) : (
            <ul className="space-y-3">
              {missing.map((row) => (
                <li key={row.field}>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-mono text-xs text-ink-700 dark:text-ink-200">
                      {row.field}
                    </span>
                    <span className="shrink-0 text-sm font-semibold tabular-nums text-ink-900 dark:text-ink-50">
                      {formatNumber(row.times_missing)}
                    </span>
                  </div>
                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-ink-200 dark:bg-ink-800">
                    <div
                      className="h-full rounded-full bg-amber-500"
                      style={{
                        width: `${mostMissing ? (row.times_missing / mostMissing) * 100 : 0}%`,
                      }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card
        title="Services identified"
        description="One universal bot — a single conversation can produce more than one of these."
        className="mt-6"
      >
        {services.length === 0 ? (
          <p className="py-10 text-center text-sm text-ink-500">
            No services identified in this window.
          </p>
        ) : (
          <ul className="grid gap-x-8 gap-y-3 sm:grid-cols-2">
            {services.map((row) => (
              <li key={row.service}>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="truncate text-sm text-ink-700 dark:text-ink-200">
                    {row.service === '(unidentified)'
                      ? 'Unidentified'
                      : formatService(row.service)}
                  </span>
                  <span className="shrink-0 text-xs tabular-nums text-ink-500">
                    {formatNumber(row.leads)} ·{' '}
                    <span className="text-emerald-600 dark:text-emerald-400">
                      {formatNumber(row.qualified)} qualified
                    </span>
                  </span>
                </div>
                <div className="mt-1.5 flex h-1.5 overflow-hidden rounded-full bg-ink-200 dark:bg-ink-800">
                  {/* Qualified sits inside the total rather than beside it, so
                      the bar reads as "how much of this converted". */}
                  <div
                    className="h-full bg-emerald-500"
                    style={{ width: `${topService ? (row.qualified / topService) * 100 : 0}%` }}
                  />
                  <div
                    className="h-full bg-brand-400/50"
                    style={{
                      width: `${topService ? ((row.leads - row.qualified) / topService) * 100 : 0}%`,
                    }}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
